import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  BuildingOfficeIcon,
  XCircleIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  IdentificationIcon,
  StarIcon as StarIconOutline
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useAppData } from '../../contexts/AppDataContext';

const SupplierForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { addSupplier, updateSupplier, getSupplierById, fetchAllData } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [supplier, setSupplier] = useState({
    name: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    wilaya: '',
    category: '',
    paymentTerms: 'immediate',
    nif: '',
    rc: '',
    ai: '',
    website: '',
    notes: '',
    logo: null,
    logoPreview: null,
    status: 'active',
    rating: 0, // Ajout de la notation sur 5 étoiles
    // Ajout de champs pour le suivi des performances
    avgDeliveryTime: 0,
    onTimeDeliveryRate: 0,
    qualityRating: 0,
    priceCompetitiveness: 0,
    communicationRating: 0
  });
  
  // Liste des wilayas d'Algérie
  const wilayas = [
    'Adrar', 'Chlef', 'Laghouat', 'Oum El Bouaghi', 'Batna', 'Béjaïa', 'Biskra', 'Béchar', 'Blida', 'Bouira',
    'Tamanrasset', 'Tébessa', 'Tlemcen', 'Tiaret', 'Tizi Ouzou', 'Alger', 'Djelfa', 'Jijel', 'Sétif', 'Saïda',
    'Skikda', 'Sidi Bel Abbès', 'Annaba', 'Guelma', 'Constantine', 'Médéa', 'Mostaganem', 'M\'Sila', 'Mascara', 'Ouargla',
    'Oran', 'El Bayadh', 'Illizi', 'Bordj Bou Arreridj', 'Boumerdès', 'El Tarf', 'Tindouf', 'Tissemsilt', 'El Oued', 'Khenchela',
    'Souk Ahras', 'Tipaza', 'Mila', 'Aïn Defla', 'Naâma', 'Aïn Témouchent', 'Ghardaïa', 'Relizane', 'Timimoun',
    'Bordj Badji Mokhtar', 'Ouled Djellal', 'Béni Abbès', 'In Salah', 'In Guezzam', 'Touggourt', 'Djanet', 'El M\'Ghair', 'El Meniaa'
  ];

  // Catégories de fournisseurs
  const categories = [
    'Matériel de bureau', 
    'Informatique', 
    'Fournitures', 
    'Mobilier', 
    'Services', 
    'Transport', 
    'Production',
    'Autre'
  ];

  // Conditions de paiement
  const paymentTerms = [
    { value: 'immediate', label: 'Paiement immédiat' },
    { value: '15days', label: '15 jours' },
    { value: '30days', label: '30 jours' },
    { value: '60days', label: '60 jours' },
    { value: '90days', label: '90 jours' }
  ];

  // État pour stocker les produits associés à ce fournisseur
  const [associatedProducts, setAssociatedProducts] = useState([]);

  // Charger les données du fournisseur en mode édition
  useEffect(() => {
    if (isEditMode) {
      setIsLoading(true);
      
      // Méthode 1: Essayer d'abord de récupérer le fournisseur via le contexte
      console.log("Recherche du fournisseur avec ID:", id);
      let existingSupplier = getSupplierById(id);
      
      // Méthode 2: Si échec, essayer directement depuis localStorage (avec les deux clés possibles)
      if (!existingSupplier) {
        console.log("Fournisseur non trouvé dans le contexte, recherche dans localStorage...");
        try {
          // Try both possible storage keys
          let storedSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
          
          if (!storedSuppliers || storedSuppliers.length === 0) {
            storedSuppliers = JSON.parse(localStorage.getItem('suppliers') || '[]');
          }
          
          // Try to match by ID (as string or number)
          existingSupplier = storedSuppliers.find(s => String(s.id) === String(id));
          
          console.log("Fournisseurs dans localStorage:", storedSuppliers.length);
          console.log("Fournisseur trouvé dans localStorage:", existingSupplier);
        } catch (error) {
          console.error("Erreur lors de la lecture de localStorage:", error);
        }
      } else {
        console.log("Fournisseur trouvé via contexte:", existingSupplier);
      }
      
      if (existingSupplier) {
        setSupplier(existingSupplier);
        
        // Charger les produits associés
        loadAssociatedProducts(existingSupplier.id, existingSupplier.name);
      } else {
        toast.error('Fournisseur introuvable');
        navigate('/suppliers');
      }
      
      setIsLoading(false);
    }
  }, [isEditMode, id, getSupplierById, navigate]);
  
  // Fonction pour charger les produits associés à ce fournisseur
  const loadAssociatedProducts = (supplierId, supplierName) => {
    try {
      // Récupérer tous les produits (essayer plusieurs clés possibles)
      const allProducts = JSON.parse(localStorage.getItem('stockpro_products') || '[]') || 
                          JSON.parse(localStorage.getItem('products') || '[]');
      
      console.log(`Recherche de produits pour le fournisseur ID: ${supplierId}, Nom: ${supplierName}`);
      console.log(`Total produits disponibles: ${allProducts.length}`);
      
      // Filtrer les produits associés à ce fournisseur (avec plus de souplesse)
      const products = allProducts.filter(p => 
        (p.supplierId && p.supplierId === supplierId) || 
        (p.supplier && p.supplier.toLowerCase() === supplierName.toLowerCase()) ||
        (p.supplierName && p.supplierName.toLowerCase() === supplierName.toLowerCase())
      );
      
      setAssociatedProducts(products);
      console.log(`${products.length} produits associés à ce fournisseur chargés`);
    } catch (error) {
      console.error('Erreur lors du chargement des produits associés:', error);
    }
  };

  // Gérer les changements de champs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSupplier({
      ...supplier,
      [name]: value
    });
  };

  // Gérer le changement de logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSupplier({
        ...supplier,
        logo: file,
        logoPreview: URL.createObjectURL(file)
      });
    }
  };

  // Supprimer le logo
  const handleRemoveLogo = () => {
    setSupplier({
      ...supplier,
      logo: null,
      logoPreview: null
    });
  };

  // Gérer la notation par étoiles
  const handleRatingChange = (rating) => {
    setSupplier({
      ...supplier,
      rating
    });
  };

  // Gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!supplier.name) {
      toast.error('Veuillez saisir le nom du fournisseur');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Préparer l'objet fournisseur avec un format cohérent
      // Convert contact field appropriately if needed
      const contactField = supplier.contactName || supplier.contact || '';
      
      // Ensure we have an ID for new suppliers
      let supplierId = supplier.id;
      if (!isEditMode || !supplierId) {
        const prefix = 'SUP';
        const randomNum = Math.floor(Math.random() * 900) + 100; // Random number between 100-999
        supplierId = `${prefix}-${randomNum}`;
      }
      
      const formattedSupplier = {
        ...supplier,
        // Make sure we have consistent field names
        id: supplierId,
        contact: contactField,
        contactName: contactField,
        // Make status field consistent
        status: supplier.status === 'active' ? 'Active' : 'Inactive',
        // Assurer que les champs numériques sont des nombres
        rating: parseFloat(supplier.rating) || 0,
        avgDeliveryTime: parseFloat(supplier.avgDeliveryTime) || 0,
        onTimeDeliveryRate: parseFloat(supplier.onTimeDeliveryRate) || 0,
        qualityRating: parseFloat(supplier.qualityRating) || 0,
        priceCompetitiveness: parseFloat(supplier.priceCompetitiveness) || 0,
        communicationRating: parseFloat(supplier.communicationRating) || 0,
        // Default values for required fields in the detail view
        orders: supplier.orders || 0,
        lastOrder: supplier.lastOrder || new Date().toISOString().split('T')[0],
        // Ajouter des champs pour l'intégration avec d'autres modules
        lastUpdated: new Date().toISOString(),
        createdAt: supplier.createdAt || new Date().toISOString(),
        associatedProducts: [] // Sera rempli plus tard par les relations produit-fournisseur
      };
      
      console.log('Saving supplier:', formattedSupplier);
      
      // Save directly to localStorage as a fallback mechanism
      const directSave = () => {
        try {
          // Get current suppliers from localStorage
          const currentSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
          
          if (isEditMode) {
            // Update existing supplier
            const index = currentSuppliers.findIndex(s => String(s.id) === String(supplierId));
            if (index !== -1) {
              currentSuppliers[index] = formattedSupplier;
            } else {
              currentSuppliers.push(formattedSupplier);
            }
          } else {
            // Add new supplier
            currentSuppliers.push(formattedSupplier);
          }
          
          // Save to both storage keys
          localStorage.setItem('stockpro_suppliers', JSON.stringify(currentSuppliers));
          localStorage.setItem('suppliers', JSON.stringify(currentSuppliers));
          
          console.log('Supplier saved directly to localStorage:', formattedSupplier);
          return true;
        } catch (error) {
          console.error('Error saving supplier directly to localStorage:', error);
          return false;
        }
      };
      
      let success = false;
      
      if (isEditMode) {
        // Mise à jour d'un fournisseur existant
        try {
          const updatedSupplier = await updateSupplier(supplier.id, formattedSupplier);
          console.log('Supplier updated successfully via context:', updatedSupplier);
          success = true;
          
          // Mettre à jour les produits associés à ce fournisseur
          updateAssociatedProducts(formattedSupplier);
        } catch (updateError) {
          console.error('Error updating supplier via context:', updateError);
          // Try direct save as fallback
          success = directSave();
        }
      } else {
        // Création d'un nouveau fournisseur
        try {
          const newSupplier = await addSupplier(formattedSupplier);
          console.log('New supplier added successfully via context:', newSupplier);
          success = true;
        } catch (addError) {
          console.error('Error adding supplier via context:', addError);
          // Try direct save as fallback
          success = directSave();
        }
      }
      
      if (success) {
        toast.success(`Fournisseur ${supplier.name} ${isEditMode ? 'mis à jour' : 'ajouté'} avec succès`);
        
        // Rafraîchir les données pour s'assurer que l'UI est à jour
        await fetchAllData();
        
        // Vérifier que les données sont bien sauvegardées
        const storedSuppliers = JSON.parse(localStorage.getItem('stockpro_suppliers') || '[]');
        console.log('Suppliers in localStorage after save:', storedSuppliers);
        
        // Petit délai pour assurer que le contexte est mis à jour avant la navigation
        setTimeout(() => {
          navigate('/suppliers');
        }, 1000);
      } else {
        toast.error('Échec de l\'enregistrement du fournisseur');
      }
      
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast.error(`Erreur lors de l'enregistrement du fournisseur: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fonction pour mettre à jour les produits associés à ce fournisseur
  const updateAssociatedProducts = (supplierData) => {
    try {
      // Récupérer tous les produits
      const allProducts = JSON.parse(localStorage.getItem('products') || '[]');
      
      // Filtrer les produits associés à ce fournisseur
      const associatedProducts = allProducts.filter(p => 
        p.supplierId === supplierData.id || 
        p.supplier === supplierData.name
      );
      
      if (associatedProducts.length > 0) {
        console.log(`${associatedProducts.length} produits associés à ce fournisseur trouvés`);
        
        // Mettre à jour le nom du fournisseur dans les produits si nécessaire
        const updatedProducts = allProducts.map(p => {
          if (p.supplierId === supplierData.id) {
            return { ...p, supplier: supplierData.name };
          }
          return p;
        });
        
        // Sauvegarder les produits mis à jour
        localStorage.setItem('products', JSON.stringify(updatedProducts));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des produits associés:', error);
    }
  };
  
  // Rendu des étoiles de notation
  const renderRatingStars = () => {
    return Array(5).fill(0).map((_, i) => (
      <button
        type="button"
        key={i}
        onClick={() => handleRatingChange(i + 1)}
        className="focus:outline-none"
      >
        {i < supplier.rating ? (
          <StarIconSolid className="h-8 w-8 text-amber-400" />
        ) : (
          <StarIconOutline className="h-8 w-8 text-white/40 hover:text-white/60" />
        )}
      </button>
    ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/suppliers')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
            </h1>
            <p className="text-white/60 mt-1">
              {isEditMode ? 'Modifier les informations du fournisseur' : 'Ajouter un nouveau fournisseur au système'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Colonne principale - Informations du fournisseur */}
              <div className="lg:col-span-2 space-y-6">
                {/* Informations générales */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Informations générales</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="name">
                        Nom du fournisseur
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={supplier.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-white/30"
                        placeholder="Nom de l'entreprise"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="category">
                        Catégorie
                      </label>
                      <select
                        name="category"
                        id="category"
                        value={supplier.category}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sélectionnez une catégorie</option>
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="contactName">
                        Personne à contacter
                      </label>
                      <input
                        type="text"
                        name="contactName"
                        id="contactName"
                        value={supplier.contactName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-white/30"
                        placeholder="Nom et prénom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="email">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={supplier.email}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-white/30"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="phone">
                        Téléphone
                      </label>
                      <input
                        type="text"
                        name="phone"
                        id="phone"
                        value={supplier.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-white/30"
                        placeholder="0XXX XX XX XX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="website">
                        Site web
                      </label>
                      <input
                        type="text"
                        name="website"
                        id="website"
                        value={supplier.website}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-white/30"
                        placeholder="www.example.com"
                      />
                    </div>
                  </div>
                </div>

                {/* Notation et performance */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Évaluation du fournisseur</h2>

                  <div className="space-y-6">
                    {/* Système de notation 5 étoiles */}
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-3">
                        Note globale
                      </label>
                      <div className="flex items-center space-x-2">
                        {renderRatingStars()}
                        <span className="ml-2 text-white text-lg font-medium">{supplier.rating}/5</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="avgDeliveryTime">
                          Temps moyen de livraison (jours)
                        </label>
                        <input
                          type="number"
                          name="avgDeliveryTime"
                          id="avgDeliveryTime"
                          value={supplier.avgDeliveryTime}
                          onChange={handleInputChange}
                          min="0"
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="onTimeDeliveryRate">
                          Taux de livraison à temps (%)
                        </label>
                        <input
                          type="number"
                          name="onTimeDeliveryRate"
                          id="onTimeDeliveryRate"
                          value={supplier.onTimeDeliveryRate}
                          onChange={handleInputChange}
                          min="0"
                          max="100"
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="qualityRating">
                          Note qualité (sur 5)
                        </label>
                        <input
                          type="number"
                          name="qualityRating"
                          id="qualityRating"
                          value={supplier.qualityRating}
                          onChange={handleInputChange}
                          min="0"
                          max="5"
                          step="0.1"
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="priceCompetitiveness">
                          Compétitivité prix (sur 5)
                        </label>
                        <input
                          type="number"
                          name="priceCompetitiveness"
                          id="priceCompetitiveness"
                          value={supplier.priceCompetitiveness}
                          onChange={handleInputChange}
                          min="0"
                          max="5"
                          step="0.1"
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="communicationRating">
                          Communication (sur 5)
                        </label>
                        <input
                          type="number"
                          name="communicationRating"
                          id="communicationRating"
                          value={supplier.communicationRating}
                          onChange={handleInputChange}
                          min="0"
                          max="5"
                          step="0.1"
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Adresse */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Adresse</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="address">
                        Adresse
                      </label>
                      <input
                        type="text"
                        name="address"
                        id="address"
                        value={supplier.address}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Numéro et rue"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="city">
                        Ville
                      </label>
                      <input
                        type="text"
                        name="city"
                        id="city"
                        value={supplier.city}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="postalCode">
                        Code postal
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        id="postalCode"
                        value={supplier.postalCode}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="wilaya">
                        Wilaya
                      </label>
                      <select
                        name="wilaya"
                        id="wilaya"
                        value={supplier.wilaya}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sélectionnez une wilaya</option>
                        {wilayas.map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Informations fiscales */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Informations fiscales</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="nif">
                        NIF
                      </label>
                      <input
                        type="text"
                        name="nif"
                        id="nif"
                        value={supplier.nif}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="rc">
                        Registre de Commerce
                      </label>
                      <input
                        type="text"
                        name="rc"
                        id="rc"
                        value={supplier.rc}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="ai">
                        Article d'imposition
                      </label>
                      <input
                        type="text"
                        name="ai"
                        id="ai"
                        value={supplier.ai}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/70 mb-2" htmlFor="paymentTerms">
                        Conditions de paiement
                      </label>
                      <select
                        name="paymentTerms"
                        id="paymentTerms"
                        value={supplier.paymentTerms}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {paymentTerms.map((term) => (
                          <option key={term.value} value={term.value}>
                            {term.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Colonne secondaire - Logo et notes */}
              <div className="space-y-6">
                {/* Logo */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Logo</h2>

                  <div className="flex flex-col items-center justify-center">
                    {supplier.logoPreview ? (
                      <div className="relative">
                        <img
                          src={supplier.logoPreview}
                          alt="Logo preview"
                          className="w-full h-48 object-contain rounded-xl"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="w-full h-48 bg-white/5 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => document.getElementById('logo-upload').click()}
                      >
                        <BuildingOfficeIcon className="h-12 w-12 text-white/40" />
                        <p className="mt-2 text-sm text-white/60">Cliquez pour ajouter un logo</p>
                        <p className="text-xs text-white/40 mt-1">PNG, JPG ou SVG. Max 1MB</p>
                      </div>
                    )}
                    <input
                      type="file"
                      id="logo-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoChange}
                    />
                  </div>
                </div>

                {/* Produits associés */}
                {isEditMode && associatedProducts.length > 0 && (
                  <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                    <h2 className="text-xl font-semibold text-white mb-6">Produits associés</h2>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {associatedProducts.map((product) => (
                        <div 
                          key={product.id}
                          className="flex items-center justify-between bg-white/5 p-3 rounded-xl hover:bg-white/10 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-white font-medium">{product.name}</p>
                              <p className="text-white/60 text-xs">{product.id}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{product.price?.toLocaleString() || '0'} DA</p>
                            <p className="text-white/60 text-xs">Stock: {product.stock || '0'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-4 text-sm text-white/60">
                      Ces produits sont liés à ce fournisseur. Les modifications du nom du fournisseur seront répercutées sur ces produits.
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Notes</h2>

                  <div>
                    <textarea
                      name="notes"
                      id="notes"
                      rows={5}
                      value={supplier.notes}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Notes ou commentaires sur ce fournisseur..."
                    ></textarea>
                  </div>
                </div>

                {/* Statut */}
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-6">Statut</h2>

                  <div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="status-active"
                          name="status"
                          value="active"
                          checked={supplier.status === 'active'}
                          onChange={handleInputChange}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-white/20 bg-white/10"
                        />
                        <label htmlFor="status-active" className="ml-2 text-white">
                          Actif
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          id="status-inactive"
                          name="status"
                          value="inactive"
                          checked={supplier.status === 'inactive'}
                          onChange={handleInputChange}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-white/20 bg-white/10"
                        />
                        <label htmlFor="status-inactive" className="ml-2 text-white">
                          Inactif
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/suppliers')}
                className="px-4 py-2 border border-white/20 rounded-xl text-white hover:bg-white/10 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Traitement...
                  </span>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SupplierForm; 